import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import INDIA_TOPOJSON from "../../india.json";

const StateMap = ({ data }) => {
    const [hoverInfo, setHoverInfo] = useState(null);
    const [selectedState, setSelectedState] = useState(null);

    // ✅ Normalize inconsistent state names
    const normalizeStateName = (name) => {
        const map = {
            Orissa: "Odisha",
            Chattisgarh: "Chhattisgarh",
            "NCT of Delhi": "Delhi",
            "Jammu & Kashmir": "Jammu and Kashmir",
            "Andaman & Nicobar Islands": "Andaman and Nicobar Islands",
            "Dadra & Nagar Haveli": "Dadra and Nagar Haveli",
            "Dadra and Nagar Haveli and Daman and Diu":
                "Dadra and Nagar Haveli and Daman and Diu",
            Pondicherry: "Puducherry",
        };
        return map[name?.trim()] || name?.trim();
    };

    // ✅ Convert API array → normalized map { stateName: doc_count }
    const stateComplaintsMap =
        data?.reduce((acc, item) => {
            const normalizedName = normalizeStateName(item.key);
            acc[normalizedName] = item.doc_count;
            return acc;
        }, {}) || {};

    // ✅ Determine color scale based on min/max
    const counts = Object.values(stateComplaintsMap);
    const minCount = Math.min(...counts, 0);
    const maxCount = Math.max(...counts, 1);

    // Light–dark blue gradient
    const lightColor = { r: 198, g: 223, b: 255 };
    const darkColor = { r: 0, g: 76, b: 153 };

    const getColor = (count) => {
        if (!count) return "#E0E0E0"; // gray for 0
        const ratio = (count - minCount) / (maxCount - minCount || 1);
        const r = Math.round(lightColor.r + (darkColor.r - lightColor.r) * ratio);
        const g = Math.round(lightColor.g + (darkColor.g - lightColor.g) * ratio);
        const b = Math.round(lightColor.b + (darkColor.b - lightColor.b) * ratio);
        return `rgb(${r},${g},${b})`;
    };

    return (
        <div className="flex justify-center h-full relative">
            <div>
                <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{ center: [80, 23], scale: 1000 }}
                    width={620}
                    style={{ width: "95%", height: "94%" }}
                >
                    <Geographies geography={INDIA_TOPOJSON}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const stateName = geo.properties.st_nm;
                                const complaintCount = stateComplaintsMap[stateName] || 0;
                                const centroid = geoCentroid(geo);

                                return (
                                    <React.Fragment key={geo.rsmKey}>
                                        <Geography
                                            geography={geo}
                                            onMouseEnter={(e) => {
                                                const { clientX, clientY } = e;
                                                setHoverInfo({
                                                    state: stateName,
                                                    complaints: complaintCount,
                                                    x: clientX,
                                                    y: clientY,
                                                });
                                            }}
                                            onMouseMove={(e) => {
                                                const { clientX, clientY } = e;
                                                setHoverInfo((prev) => ({
                                                    ...prev,
                                                    x: clientX,
                                                    y: clientY,
                                                }));
                                            }}
                                            onMouseLeave={() => setHoverInfo(null)}
                                            onClick={() => {
                                                if (complaintCount > 0) {
                                                    setSelectedState({
                                                        state: stateName,
                                                        complaints: complaintCount,
                                                    });
                                                }
                                            }}
                                            style={{
                                                default: {
                                                    fill: getColor(complaintCount),
                                                    stroke: "#000",
                                                    strokeWidth: 0.5,
                                                },
                                                hover: { fill: "#FFCC00" },
                                            }}
                                        />

                                        {/* ✅ (Optional) Labels can be re-enabled if needed */}
                                        {centroid && (
                                            <Marker coordinates={centroid}>
                                                {/* Uncomment if you want numbers displayed */}
                                                {/* <text
                          textAnchor="middle"
                          y={5}
                          style={{
                            fontFamily: "Arial, sans-serif",
                            fontSize: "10px",
                            fill: "#000",
                            fontWeight: "600",
                            textShadow:
                              "1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)",
                            pointerEvents: "none",
                          }}
                        >
                          {complaintCount > 0 ? complaintCount : ""}
                        </text> */}
                                            </Marker>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        }
                    </Geographies>
                </ComposableMap>

                {/* Tooltip on hover */}
                {hoverInfo && (
                    <div
                        className="fixed bg-white p-2 rounded shadow pointer-events-none z-[1000]"
                        style={{
                            top: hoverInfo.y + 10,
                            left: hoverInfo.x + 10,
                            fontSize: "12px",
                        }}
                    >
                        <p className="font-semibold">{hoverInfo.state}</p>
                        <p>No. of Complaints: {hoverInfo.complaints}</p>
                    </div>
                )}

                {/* Popup on click */}
                {selectedState && (
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg z-[2000] min-w-[220px] text-center text-sm">
                        <h3 className="font-bold text-white text-md p-2 rounded-t-lg bg-gradient-to-r from-[#2670DF] to-[#677eca]">
                            {selectedState.state}
                        </h3>
                        <table className="block min-w-full table-auto bg-white rounded-lg my-1 border border-gray-800 p-2 text-xs">
                            <thead>
                                <tr className="text-black p-2 rounded-lg opacity-80">
                                    <th className="border border-gray-400 bg-slate-300 px-2 whitespace-nowrap">
                                        Description
                                    </th>
                                    <th className="border border-gray-400 bg-slate-300 px-2">
                                        Value
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="bg-white hover:bg-gray-100">
                                    <td className="border border-b border-gray-400 text-xs px-1">
                                        Total Complaints
                                    </td>
                                    <td className="border border-b border-gray-400 text-xs px-1">
                                        {selectedState.complaints}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <button
                            className="text-[#7A9ED6] px-2 rounded underline text-xs mb-2"
                            onClick={() => setSelectedState(null)}
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StateMap;
